// @mui
import Card from '@mui/material/Card';
import { Box, Paper, TextField, Grid, Stack, Button, Typography, CircularProgress } from '@mui/material';
import CardContent from '@mui/material/CardContent';
// hooks
import { useEffect, useState, useRef } from 'react';
// components
import Iconify from 'src/components/iconify';
import { getRequest, postRequestForWebsite } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { handleErrorMessages } from 'src/services/fetching';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { useSelector } from 'react-redux';
import useFieldManagementSection from 'src/hooks/use-field-management-page';

// ----------------------------------------------------------------------

export default function FieldManagement() {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const lastFeatureSectionRef = useRef(null);

  const fieldManagementPageData = useSelector((state) => state.website.fieldManagementPage);
  const { fetchOnMount } = useFieldManagementSection();

  const [formData, setFormData] = useState({
    // Main Header
    mainTitle: "",
    mainTitleColor: "#000000",
    subtitle: "",
    subtitleColor: "#666666",
    description: "",
    descriptionColor: "#666666",

    // Key Features Section
    keyFeaturesTitle: "",
    keyFeaturesTitleColor: "#000000",

    // Feature Categories
    featureCategories: [
      {
        id: '',
        title: "",
        titleColor: "#4CAF50",
        items: [
          {
            id: '',
            text: "",
            textColor: "#333333"
          },
        ]
      },

    ],

    // Bottom Section
    bottomTitle: "",
    bottomTitleColor: "#000000",
    bottomDescription: "",
    bottomDescriptionColor: "#666666"
  });

  const updateFormData = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (typeof current[key] !== 'object' || Array.isArray(current[key])) {
          current[key] = {};
        }
        current = current[key];
      }
      current[keys[keys.length - 1]] = value;

      return newData;
    });
  };

  const updateFeatureCategory = (categoryIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      featureCategories: prev.featureCategories.map((category, i) =>
        i === categoryIndex ? { ...category, [field]: value } : category
      )
    }));
  };

  const updateFeatureItem = (categoryIndex, itemIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      featureCategories: prev.featureCategories.map((category, i) =>
        i === categoryIndex ? {
          ...category,
          items: category.items.map((item, j) =>
            j === itemIndex ? { ...item, [field]: value } : item
          )
        } : category
      )
    }));
  };

  const addFeatureCategory = () => {
    const newCategory = {
      id: `category-${Date.now()}`,
      title: "",
      titleColor: "#4CAF50",
      items: [
        {
          id: `item-${Date.now()}`,
          text: "",
          textColor: "#333333"
        }
      ]
    };

    setFormData(prev => ({
      ...prev,
      featureCategories: [...prev.featureCategories, newCategory]
    }));

    setTimeout(() => {
      if (lastFeatureSectionRef.current) {
        lastFeatureSectionRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const removeFeatureCategory = (categoryIndex) => {
    setFormData(prev => ({
      ...prev,
      featureCategories: prev.featureCategories.filter((_, i) => i !== categoryIndex)
    }));
  };

  const addFeatureItem = (categoryIndex) => {
    const newItem = {
      id: `item-${Date.now()}`,
      text: "",
      textColor: "#333333"
    };

    setFormData(prev => ({
      ...prev,
      featureCategories: prev.featureCategories.map((category, i) =>
        i === categoryIndex ? {
          ...category,
          items: [...category.items, newItem]
        } : category
      )
    }));
  };

  const removeFeatureItem = (categoryIndex, itemIndex) => {
    setFormData(prev => ({
      ...prev,
      featureCategories: prev.featureCategories.map((category, i) =>
        i === categoryIndex ? {
          ...category,
          items: category.items.filter((_, j) => j !== itemIndex)
        } : category
      )
    }));
  };


  useEffect(() => {
    fetchOnMount();
  }, [fetchOnMount]);

  useEffect(() => {
    if (fieldManagementPageData?.field_management_page) {
      // Create a deep copy to avoid mutating the read-only Redux store data
      const deepCopy = JSON.parse(JSON.stringify(fieldManagementPageData.field_management_page));
      setFormData(prev => ({
        ...prev,
        ...deepCopy
      }));
    }
  }, [fieldManagementPageData])

  const handleSave = async () => {
    setLoading(true);

    try {
      // Create a FormData object for file uploads
      const formDataToSend = new FormData();

      // Append all form data
      Object.entries(formData).forEach(([key, value]) => {
        // Skip heroImage as we'll handle it separately
        if (key !== 'heroImage') {
          // Handle nested objects like primaryButton and secondaryButton
          if (typeof value === 'object' && value !== null && !(value instanceof File)) {
            formDataToSend.append(key, JSON.stringify(value));
          } else {
            formDataToSend.append(key, value);
          }
        }
      });

      // Append the hero image if it exists
      if (formData.heroImage) {
        formDataToSend.append('heroImage', formData.heroImage);
      }

      // Make sure heroImageUrl is included in the submission
      if (formData.heroImageUrl) {
        formDataToSend.append('heroImageUrl', formData.heroImageUrl);
      }

      const response = await postRequestForWebsite(
        endpoints.admin_dashboard.website.field_management_page.field_management_section,
        // formDataToSend,
        // dataForHeroSection,
        {
          attributes: {
            ...JSON.parse(JSON.stringify(fieldManagementPageData)),
            field_management_page: formData
          }
        },
        true
      );

      if (response.metadata?.success) {
        enqueueSnackbar({
          variant: 'success',
          message: response.metadata.message,
        });
      } else {
        handleErrorMessages(response.message || 'An error occurred.');
      }
    } catch (err) {
      console.error('Error:', err);
      handleErrorMessages(err.message || 'Submission failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h4" paragraph>
          Field Management - Daily Field Report
        </Typography>

        {/* Main Header Section */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Main Header</Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Main Title"
                value={formData.mainTitle}
                onChange={(e) => updateFormData('mainTitle', e.target.value)}
                placeholder="Enter main title"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Title Color"
                type="color"
                value={formData.mainTitleColor}
                onChange={(e) => updateFormData('mainTitleColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>

            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Subtitle"
                value={formData.subtitle}
                onChange={(e) => updateFormData('subtitle', e.target.value)}
                placeholder="Enter subtitle"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Subtitle Color"
                type="color"
                value={formData.subtitleColor}
                onChange={(e) => updateFormData('subtitleColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>

            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => updateFormData('description', e.target.value)}
                placeholder="Enter main description"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Description Color"
                type="color"
                value={formData.descriptionColor}
                onChange={(e) => updateFormData('descriptionColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Key Features Header */}
        <Box sx={{ mb: 3, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Key Features Section</Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Key Features Title"
                value={formData.keyFeaturesTitle}
                onChange={(e) => updateFormData('keyFeaturesTitle', e.target.value)}
                placeholder="Enter key features title"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Title Color"
                type="color"
                value={formData.keyFeaturesTitleColor}
                onChange={(e) => updateFormData('keyFeaturesTitleColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Feature Categories */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center">
              <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
              <Typography variant="h6">Feature Categories</Typography>
            </Stack>
            <Button
              variant="contained"
              color="success"
              startIcon={<Iconify icon="mingcute:add-line" />}
              onClick={addFeatureCategory}
            >
              Add Feature Category
            </Button>
          </Stack>

          {formData.featureCategories.map((category, categoryIndex, array) => (
            <Paper
              key={category.id}
              variant="outlined"
              sx={{ p: 3, mb: 3, bgcolor: 'background.neutral' }}
              ref={categoryIndex === array.length - 1 ? lastFeatureSectionRef : null}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" color="primary">
                  Feature Category {categoryIndex + 1}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={() => removeFeatureCategory(categoryIndex)}
                    startIcon={<Iconify icon="mingcute:close-line" />}
                  >
                    Remove
                  </Button>
                </Stack>
              </Stack>

              {/* Category Header */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Category ID"
                    value={category.id}
                    onChange={(e) => updateFeatureCategory(categoryIndex, 'id', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Category Title"
                    value={category.title}
                    onChange={(e) => updateFeatureCategory(categoryIndex, 'title', e.target.value)}
                    placeholder="Enter category title"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Title Color"
                    type="color"
                    value={category.titleColor}
                    onChange={(e) => updateFeatureCategory(categoryIndex, 'titleColor', e.target.value)}
                    sx={{ '& input': { height: 20 } }}
                  />
                </Grid>
              </Grid>

              {/* Category Items */}
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Feature Items ({category.items.length})
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    startIcon={<Iconify icon="mingcute:add-line" />}
                    onClick={() => addFeatureItem(categoryIndex)}
                  >
                    Add Item
                  </Button>
                </Stack>

                {category.items.map((item, itemIndex) => (
                  <Paper
                    key={item.id}
                    variant="outlined"
                    sx={{ p: 2, mb: 2, bgcolor: 'background.paper' }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="caption" color="primary">
                        Item {itemIndex + 1}
                      </Typography>
                      <Stack direction="row" spacing={0.5}>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => removeFeatureItem(categoryIndex, itemIndex)}
                          sx={{ minWidth: 25, p: 0.2 }}
                        >
                          ×
                        </Button>
                      </Stack>
                    </Stack>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Item ID"
                          size="small"
                          value={item.id}
                          onChange={(e) => updateFeatureItem(categoryIndex, itemIndex, 'id', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={7}>
                        <TextField
                          fullWidth
                          label="Item Text"
                          multiline
                          rows={2}
                          size="small"
                          value={item.text}
                          onChange={(e) => updateFeatureItem(categoryIndex, itemIndex, 'text', e.target.value)}
                          placeholder="Enter feature description"
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          label="Text Color"
                          type="color"
                          size="small"
                          value={item.textColor}
                          onChange={(e) => updateFeatureItem(categoryIndex, itemIndex, 'textColor', e.target.value)}
                          sx={{ '& input': { height: 20 } }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Box>
            </Paper>
          ))}
        </Box>

        {/* Bottom Section */}
        <Box sx={{ mb: 5 }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Bottom Section</Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Bottom Title (Optional)"
                value={formData.bottomTitle}
                onChange={(e) => updateFormData('bottomTitle', e.target.value)}
                placeholder="Enter bottom section title (leave empty if not needed)"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Title Color"
                type="color"
                value={formData.bottomTitleColor}
                onChange={(e) => updateFormData('bottomTitleColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>

            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Bottom Description"
                multiline
                rows={3}
                value={formData.bottomDescription}
                onChange={(e) => updateFormData('bottomDescription', e.target.value)}
                placeholder="Enter bottom section description"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Description Color"
                type="color"
                value={formData.bottomDescriptionColor}
                onChange={(e) => updateFormData('bottomDescriptionColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Action Buttons */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={2}
          sx={{ pt: 3, borderTop: '1px solid', borderColor: 'divider' }}
        >
          <LoadingButton
            loading={loading}
            variant="contained"
            color="primary"
            onClick={handleSave}
            startIcon={<Iconify icon="eva:save-fill" />}
            size="large"
          >
            Update
          </LoadingButton>
        </Stack>
      </CardContent>
    </Card>
  );
}
